import {
  getAdminJWTCookie as _getAdminJWTCookie,
  deleteAdminJWTCookie,
} from '@repo/utils';
import jwtDecode from 'jwt-decode';
import { getImpersonationCookie } from './impersonation';

// Wrapper with debugging for admin JWT cookie
function getAdminJWTCookie(): string | undefined {
  const result = _getAdminJWTCookie();
  return result;
}

export const JWT_LOCAL_STORAGE_KEY = 'jwtToken';

/**
 * Get JWT token with impersonation support
 * During impersonation, returns the admin JWT token from cookie
 * Otherwise returns the regular JWT token from localStorage
 */
export function getJWTToken(): string | null {
  try {
    // Check if we're impersonating
    const impersonationId = getImpersonationCookie();

    if (impersonationId) {
      // During impersonation, use admin JWT token from cookie
      const adminToken = getAdminJWTCookie();
      if (adminToken) {
        return adminToken;
      }
    }

    // Regular flow - get token from localStorage
    const localToken = localStorage.getItem(JWT_LOCAL_STORAGE_KEY);
    return localToken;
  } catch (error) {
    console.error('getJWTToken: Error getting JWT token:', error);
    // Fallback to localStorage only
    return localStorage.getItem(JWT_LOCAL_STORAGE_KEY);
  }
}

/**
 * Set JWT token in localStorage
 */
export function setJWTToken(token: string): void {
  localStorage.setItem(JWT_LOCAL_STORAGE_KEY, token);
}

/**
 * Delete JWT tokens (both localStorage and admin cookie)
 */
export function deleteJWTToken(): void {
  localStorage.removeItem(JWT_LOCAL_STORAGE_KEY);
  deleteAdminJWTCookie({ path: '/' });
}

export interface JWTUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  roles: string[];
}

/**
 * Parse the JWT token and extract the user information.
 */
export function getTokenUser(token?: string): JWTUser | null {
  if (!token) {
    return null;
  }

  try {
    const decoded = jwtDecode<JWTUser>(token);
    if (!decoded || typeof decoded !== 'object') {
      return null;
    }

    return {
      id: decoded.id,
      email: decoded.email,
      first_name: decoded.first_name,
      last_name: decoded.last_name,
      phone_number: decoded.phone_number,
      roles: decoded.roles || [],
    };
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}
